/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import APIManager from '../../ApiManager/ApiManager';
import {
  FETCH_COGNITO_USERS_REQUEST,
  FETCH_COGNITO_USERS_SUCCESS,
  FETCH_COGNITO_USERS_FAILURE,
} from './CognitoUsersTypes';

const COGNITO_USERS_CACHE_KEY = 'cognito-users-cache';

const safeParseCache = () => {
  if (typeof sessionStorage === 'undefined') return [];

  try {
    const cached = sessionStorage.getItem(COGNITO_USERS_CACHE_KEY);
    if (!cached) return [];

    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Failed to parse cognito users cache', error);
    return [];
  }
};

const persistCache = (users) => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(COGNITO_USERS_CACHE_KEY, JSON.stringify(users));
  } catch (error) {
    console.warn('Failed to persist cognito users cache', error);
  }
};

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const candidates = [
    data.data,
    data.users,
    data.items,
    data.results,
    data.content,
    data.payload,
  ];

  const firstDirectArray = candidates.find(Array.isArray);
  if (firstDirectArray) return firstDirectArray;

  const firstNestedArray = Object.values(data).find(Array.isArray);
  if (firstNestedArray) return firstNestedArray;

  return [];
};

const dedupeUsers = (...userLists) => {
  const map = new Map();

  const addUser = (user) => {
    if (!user || typeof user !== 'object') return;

    const possibleIds = [
      user.id,
      user.userId,
      user.cognitoUserId,
      user.username,
      user.userName,
      user.preferredUserName,
    ];

    const key = possibleIds.find(Boolean);
    if (!key) return;

    map.set(String(key), user);
  };

  userLists.forEach((list) => {
    toArray(list).forEach(addUser);
  });

  return Array.from(map.values());
};

const normalizeUsers = (users) => {
  return toArray(users)
    .map((user) => {
      if (!user || typeof user !== 'object') return null;

      const id =
        user.id ||
        user.userId ||
        user.cognitoUserId ||
        user.username ||
        user.userName ||
        user.preferredUserName;

      if (!id) return null;

      return {
        id: String(id),
        firstName: user.firstName || '',
        userName:
          user.userName ||
          user.username ||
          user.preferredUserName ||
          user.email ||
          '',
        role: user.role || user.roles || user.roleName || '',
        scope: user.scope || '',
        portal: user.portal || user.portalType || '',
      };
    })
    .filter(Boolean);
};

const buildIdSet = (users) => {
  const idSet = new Set();

  toArray(users).forEach((user) => {
    const ids = [
      user?.id,
      user?.userId,
      user?.cognitoUserId,
      user?.username,
      user?.userName,
      user?.preferredUserName,
    ];

    ids.forEach((id) => {
      if (!id) return;
      const normalized = String(id);
      idSet.add(normalized);
      idSet.add(normalized.toLowerCase());
    });
  });

  return idSet;
};

// Action creators
export const fetchCognitoUsersRequest = () => ({
  type: FETCH_COGNITO_USERS_REQUEST,
});

export const fetchCognitoUsersSuccess = (users) => ({
  type: FETCH_COGNITO_USERS_SUCCESS,
  payload: users,
});

export const fetchCognitoUsersFailure = (error) => ({
  type: FETCH_COGNITO_USERS_FAILURE,
  payload: error,
});

// Thunk action
export const fetchCognitoUsers = () => {
  return async (dispatch) => {
    const cachedUsers = safeParseCache();

    if (cachedUsers.length > 0) {
      dispatch(fetchCognitoUsersSuccess(cachedUsers));
      return cachedUsers;
    }

    dispatch(fetchCognitoUsersRequest());

    try {
      const queryParams = new URLSearchParams({
        sortOrder: 'ASC',
      });

      const response = await APIManager.getCognitoUsers(
        [],
        queryParams.toString()
      );

      const normalizedUsers = normalizeUsers(response);
      const mergedUsers = dedupeUsers(normalizedUsers);
      persistCache(mergedUsers);

      dispatch(fetchCognitoUsersSuccess(mergedUsers));
      return mergedUsers;
    } catch (error) {
      console.error('API Error details:', error);
      dispatch(
        fetchCognitoUsersFailure(error.message || 'Failed to fetch cognito users')
      );
      throw error;
    }
  };
};
